import React, { useState } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Chip,
    List,
    ListItem,
    Divider,
    CircularProgress,
} from '@mui/material';
import {
    MoreVert,
    CleaningServices,
    DirtyLens,
    Engineering,
    DoNotDisturb,
    Hotel,
    TransferWithinAStation,
    MeetingRoom,
    AccessTime,
    History,
} from '@mui/icons-material';
import API from '../api/axios';

function RoomCard({ room, user, statusConfig, onUpdate, showToast }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);


  const config = statusConfig[room.status];

    const actions = [
        { action: 'CLEANED', label: 'Marchează Curată', icon: <CleaningServices />, color: '#2e7d32' },
        { action: 'MARKED_DIRTY', label: 'Marchează Murdară', icon: <DirtyLens />, color: '#c62828' },
        { action: 'IN_PROGRESS', label: 'În curs de curățare', icon: <Engineering />, color: '#ef6c00' },
        { action: 'OUT_OF_ORDER', label: 'Indisponibilă', icon: <DoNotDisturb />, color: '#616161' },
        { action: 'OCCUPIED', label: 'Ocupată', icon: <Hotel />, color: '#1565c0' },
        { action: 'IN_TRANSIT', label: 'În tranzit', icon: <TransferWithinAStation />, color: '#7b1fa2' },
    ].filter((a) => {
        // Hide current status action
        if (a.action === 'CLEANED' && room.status === 'CLEAN') return false;
        if (a.action === 'MARKED_DIRTY' && room.status === 'DIRTY') return false;
        if (a.action === 'IN_PROGRESS' && room.status === 'IN_PROGRESS') return false;
        if (a.action === 'OUT_OF_ORDER' && room.status === 'OUT_OF_ORDER') return false;
        if (a.action === 'OCCUPIED' && room.status === 'OCCUPIED') return false;
        if (a.action === 'IN_TRANSIT' && room.status === 'IN_TRANSIT') return false;
        // MAID can only clean or mark in progress
        if (user.role === 'MAID' && !['CLEANED', 'IN_PROGRESS'].includes(a.action)) return false;
        return true;
    });

  const handleAction = (action) => {
    setSelectedAction(action);
    setAnchorEl(null);
    setDialogOpen(true);
  };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            await API.put(`/rooms/${room.id}/status`, {
                action: selectedAction,
                notes: notes || null,
            });
            setDialogOpen(false);
            setNotes('');
            onUpdate();
            if (showToast) showToast(`Camera ${room.roomNumber} actualizată cu succes!`);
        } catch (err) {
            const msg = err.response?.data?.message || 'Eroare la actualizare';
            if (showToast) showToast(msg, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenHistory = async () => {
        setHistoryOpen(true);
        setLogsLoading(true);
        try {
            const response = await API.get(`/cleaning-logs/room/${room.id}`);
            setLogs(response.data);
        } catch (err) {
            console.error('Error fetching logs:', err);
        } finally {
            setLogsLoading(false);
        }
    };

    return (
        <>
            <Card
                sx={{
                    borderRadius: 3,
                    border: `2px solid ${config.color}20`,
                    background: `linear-gradient(135deg, #fff 0%, ${config.bg} 100%)`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 8px 30px ${config.color}25`,
                        border: `2px solid ${config.color}40`,
                    },
                }}
            >
                <CardContent sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box
                                sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: config.color,
                                    color: '#fff',
                                }}
                            >
                                <MeetingRoom />
                            </Box>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                    {room.roomNumber}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Etaj {room.floor} • {room.roomType}
                                </Typography>
                            </Box>
                        </Box>

                        <IconButton
                            size="small"
                            onClick={(e) => setAnchorEl(e.currentTarget)}
                        >
                            <MoreVert />
                        </IconButton>
                    </Box>

                    <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Chip
                            icon={config.icon}
                            label={config.label}
                            size="small"
                            sx={{
                                backgroundColor: config.bg,
                                color: config.color,
                                fontWeight: 600,
                                border: `1px solid ${config.color}30`,
                            }}
                        />
                    </Box>
                </CardContent>
            </Card>

            {/* Actions Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                PaperProps={{
                    sx: { borderRadius: 2, minWidth: 220 },
                }}
            >
                {actions.map((action) => (
                    <MenuItem
                        key={action.action}
                        onClick={() => handleAction(action.action)}
                    >
                        <ListItemIcon sx={{ color: action.color }}>
                            {action.icon}
                        </ListItemIcon>
                        <ListItemText>{action.label}</ListItemText>
                    </MenuItem>
                ))}
                <Divider />
                <MenuItem onClick={() => { setAnchorEl(null); handleOpenHistory(); }}>
                    <ListItemIcon sx={{ color: '#1a237e' }}>
                        <History />
                    </ListItemIcon>
                    <ListItemText>Istoric curățenie</ListItemText>
                </MenuItem>
            </Menu>

            {/* Confirmation Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ fontWeight: 600 }}>
                    Actualizare cameră {room.roomNumber}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Acțiune: {actions.find((a) => a.action === selectedAction)?.label}
                    </Typography>
                    <TextField
                        fullWidth
                        label="Note (opțional)"
                        multiline
                        rows={3}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Ex: Am schimbat prosoapele, am aspirat..."
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDialogOpen(false)} color="inherit">
                        Anulează
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={loading}
                        sx={{
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
                        }}
                    >
                        {loading ? 'Se salvează...' : 'Confirmă'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* History Dialog */}
            <Dialog
                open={historyOpen}
                onClose={() => setHistoryOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ fontWeight: 600 }}>
                    <History sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Istoric cameră {room.roomNumber}
                </DialogTitle>
                <DialogContent>
                    {logsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : logs.length === 0 ? (
                        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                            Nu există istoric pentru această cameră
                        </Typography>
                    ) : (
                        <List sx={{ p: 0 }}>
                            {logs.map((log, index) => {
                                const actionLabels = {
                                    CLEANED: { label: 'Curățată', color: '#2e7d32' },
                                    MARKED_DIRTY: { label: 'Marcată murdară', color: '#c62828' },
                                    IN_PROGRESS: { label: 'În curs', color: '#ef6c00' },
                                    OUT_OF_ORDER: { label: 'Indisponibilă', color: '#616161' },
                                    OCCUPIED: { label: 'Ocupată', color: '#1565c0' },
                                    IN_TRANSIT: { label: 'În tranzit', color: '#7b1fa2' },
                                };
                                const actionConfig = actionLabels[log.action] || { label: log.action, color: '#616161' };
                                return (
                                    <React.Fragment key={log.id}>
                                        {index > 0 && <Divider />}
                                        <ListItem sx={{ px: 0, py: 1.5, flexDirection: 'column', alignItems: 'flex-start' }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 0.5 }}>
                                                <Chip
                                                    label={actionConfig.label}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: `${actionConfig.color}15`,
                                                        color: actionConfig.color,
                                                        fontWeight: 600,
                                                    }}
                                                />
                                                <Typography variant="caption" color="text.secondary">
                                                    {new Date(log.createdAt).toLocaleString('ro-RO')}
                                                </Typography>
                                            </Box>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {log.userName}
                                            </Typography>
                                            {log.notes && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {log.notes}
                                                </Typography>
                                            )}
                                        </ListItem>
                                    </React.Fragment>
                                );
                            })}
                        </List>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setHistoryOpen(false)} color="inherit">
                        Închide
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default RoomCard;
